import { useCallback, useState } from 'react';
import { submitContactForm } from '../api/contact';

const INITIAL_FORM_DATA = {
  name: '',
  email: '',
  phone: '',
  message: '',
};

export function useQuoteForm({ successMessage }) {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const turnstileEnabled = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);
  const canSubmit = !isSubmitting && (!turnstileEnabled || Boolean(captchaToken));

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaResetKey((previous) => previous + 1);
  }, []);

  const handleCaptchaVerify = useCallback((token) => {
    setCaptchaToken(token);
  }, []);

  const handleCaptchaClear = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitStatus(null);

    if (turnstileEnabled && !captchaToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the security check before sending your message.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await submitContactForm({
        ...formData,
        captchaToken,
      });
      setSubmitStatus({
        type: 'success',
        message: successMessage,
      });
      setFormData(INITIAL_FORM_DATA);
      resetCaptcha();
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message,
      });
      resetCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  const turnstileProps = {
    onVerify: handleCaptchaVerify,
    onExpire: handleCaptchaClear,
    onError: handleCaptchaClear,
    resetKey: captchaResetKey,
  };

  return {
    formData,
    handleInputChange,
    handleSubmit,
    isSubmitting,
    submitStatus,
    canSubmit,
    turnstileEnabled,
    turnstileProps,
  };
}
